import { useEffect, useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { DatePicker } from '@/components/ui/DatePicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  useDeleteEngCost,
  useEngPrints,
  useEngProjectItems,
  useSaveEngCost,
  type EngCost,
  type EngCostValues,
} from '@/features/engineering/hooks';
import { ENG_COST_CATEGORY_OPTIONS, type EngCostCategory } from '@/features/engineering/model';
import { formatError } from '@/lib/errors';
import { centsToInputDollars, inputDollarsToCents, todayIsoDate } from '@/lib/formatters';

const NONE = '__none__';

export interface EngCostDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  cost?: EngCost | null;
  canDelete?: boolean;
}

/**
 * Log or edit a project cost: hardware bought for it, materials,
 * outsourced work, tooling… Quantity × unit price pre-fills the amount
 * but the amount is what counts against the budget.
 */
export function EngCostDialog({ open, onOpenChange, projectId, cost, canDelete = false }: EngCostDialogProps) {
  const save = useSaveEngCost();
  const remove = useDeleteEngCost();
  const { data: links } = useEngProjectItems(projectId);
  const { data: prints } = useEngPrints(projectId);

  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<EngCostCategory>('hardware');
  const [vendor, setVendor] = useState('');
  const [reference, setReference] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('');
  const [amount, setAmount] = useState('');
  const [amountTouched, setAmountTouched] = useState(false);
  const [incurredOn, setIncurredOn] = useState('');
  const [itemId, setItemId] = useState<string>(NONE);
  const [printId, setPrintId] = useState<string>(NONE);

  useEffect(() => {
    if (!open) return;
    setDescription(cost?.description ?? '');
    setCategory(cost?.category ?? 'hardware');
    setVendor(cost?.vendor ?? '');
    setReference(cost?.reference ?? '');
    setQuantity(cost?.quantity == null ? '' : String(cost.quantity));
    setUnit(centsToInputDollars(cost?.unit_cents));
    setAmount(cost ? centsToInputDollars(cost.amount_cents) : '');
    setAmountTouched(!!cost);
    setIncurredOn(cost?.incurred_on ?? todayIsoDate());
    setItemId(cost?.item_id ?? NONE);
    setPrintId(cost?.print_id ?? NONE);
  }, [open, cost]);

  // Keep amount = quantity × unit price until the operator edits the amount by hand.
  useEffect(() => {
    if (amountTouched) return;
    const qty = Number(quantity);
    let unitCents: number | null = null;
    try {
      unitCents = inputDollarsToCents(unit);
    } catch {
      return;
    }
    if (quantity.trim() && Number.isFinite(qty) && qty > 0 && unitCents != null) {
      setAmount(centsToInputDollars(Math.round(qty * unitCents)));
    }
  }, [quantity, unit, amountTouched]);

  const items = useMemo(
    () => (links ?? []).map((l) => l.item).filter((i): i is NonNullable<typeof i> => !!i),
    [links],
  );
  const printOptions = useMemo(
    () => (prints ?? []).filter((p) => p.status !== 'cancelled' || p.id === cost?.print_id),
    [prints, cost?.print_id],
  );

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    let values: EngCostValues;
    try {
      const amountCents = inputDollarsToCents(amount);
      if (amountCents == null) throw new Error('Enter the amount.');
      const qtyRaw = quantity.trim();
      const qty = qtyRaw ? Number(qtyRaw) : null;
      if (qty != null && (!Number.isFinite(qty) || qty <= 0)) throw new Error('Quantity must be a positive number.');
      if (!incurredOn) throw new Error('Pick the date the cost was incurred.');
      values = {
        description,
        category,
        vendor,
        reference,
        item_id: itemId === NONE ? null : itemId,
        print_id: printId === NONE ? null : printId,
        quantity: qty,
        unit_cents: inputDollarsToCents(unit),
        amount_cents: amountCents,
        incurred_on: incurredOn,
      };
    } catch (err) {
      toast.error(formatError(err));
      return;
    }
    try {
      await save.mutateAsync({ id: cost?.id, projectId, values });
      toast.success(cost ? 'Cost updated.' : 'Cost logged.');
      onOpenChange(false);
    } catch (err) {
      toast.error(formatError(err, 'Could not save the cost.'));
    }
  };

  const handleDelete = async () => {
    if (!cost) return;
    if (!window.confirm(`Delete “${cost.description}”?`)) return;
    try {
      await remove.mutateAsync({ id: cost.id, projectId });
      toast.success('Cost deleted.');
      onOpenChange(false);
    } catch (err) {
      toast.error(formatError(err, 'Could not delete the cost.'));
    }
  };

  const busy = save.isPending || remove.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{cost ? 'Edit cost' : 'Log a cost'}</DialogTitle>
          <DialogDescription>
            Hardware, materials, outsourced work… Material for 3D prints is logged on the print itself and rolls up
            automatically.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-[1fr_200px]">
            <div className="space-y-1.5">
              <Label htmlFor="cost-description">What was it for?</Label>
              <Input
                id="cost-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. M3 heat-set inserts (200 pk)"
                maxLength={200}
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as EngCostCategory)}>
                <SelectTrigger aria-label="Cost category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENG_COST_CATEGORY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="cost-qty">Quantity</Label>
              <Input
                id="cost-qty"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                inputMode="decimal"
                placeholder="1"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cost-unit">Unit price</Label>
              <Input
                id="cost-unit"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                inputMode="decimal"
                placeholder="$ 0.00"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cost-amount">Amount</Label>
              <Input
                id="cost-amount"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  setAmountTouched(true);
                }}
                inputMode="decimal"
                placeholder="$ 0.00"
                required
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="cost-vendor">Vendor</Label>
              <Input
                id="cost-vendor"
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
                placeholder="McMaster, Amazon…"
                maxLength={120}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cost-reference">PO / invoice #</Label>
              <Input
                id="cost-reference"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Optional"
                maxLength={120}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <DatePicker value={incurredOn} onChange={setIncurredOn} placeholder="Date" aria-label="Incurred on" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Catalog item</Label>
              <Select value={itemId} onValueChange={setItemId}>
                <SelectTrigger aria-label="Catalog item">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {items.map((i) => (
                    <SelectItem key={i.id} value={i.id}>
                      <span className="font-mono text-xs text-muted-foreground">{i.sku}</span> · {i.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {items.length === 0 && (
                <p className="text-xs text-muted-foreground">Link items to the project from the Overview tab to pick them here.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Related print</Label>
              <Select value={printId} onValueChange={setPrintId}>
                <SelectTrigger aria-label="Related print">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {printOptions.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <div>
              {cost && canDelete && (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={handleDelete}
                  disabled={busy}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? 'Saving…' : cost ? 'Save changes' : 'Log cost'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
